import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Navbar, Nav, Button, Badge } from 'react-bootstrap';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

interface User {
  id_usuario: number;
  nombre_usuario: string;
  nick_usuario: string;
  tipo: number;
}

interface DashboardProps {
  user: User | null;
  onLogout: () => void;
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: '¿Cerrar sesión?',
      text: '¿Está seguro que desea cerrar sesión?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#DC3545',
      cancelButtonColor: '#6C757D'
    }).then((result: any) => {
      if (result.isConfirmed) {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        onLogout();
        navigate('/login');
      }
    });
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt' },
    { path: '/punto-venta', label: 'Punto de Venta', icon: 'fas fa-cash-register' },
    { path: '/productos', label: 'Productos', icon: 'fas fa-box' },
    { path: '/boletas', label: 'Boletas', icon: 'fas fa-receipt' },
    { path: '/reportes', label: 'Reportes', icon: 'fas fa-chart-bar' },
    { path: '/usuarios', label: 'Usuarios', icon: 'fas fa-users' }
  ];

  const getUserTypeLabel = (tipo: number) => {
    switch (tipo) {
      case 1: return 'Administrador';
      case 2: return 'Vendedor';
      case 3: return 'Supervisor';
      default: return 'Usuario';
    }
  };

  return (
    <div className="dashboard-container">
      <Navbar bg="primary" variant="dark" expand="lg" className="shadow-sm">
        <Container fluid>
          <Navbar.Brand as={Link} to="/dashboard" className="fw-bold">
            <i className="fas fa-store me-2"></i>
            SISTEMA POS
          </Navbar.Brand>
          
          <div className="d-flex align-items-center text-white">
            <div className="me-4">
              <small className="d-block text-center">
                {currentTime.toLocaleDateString('es-CL')}
              </small>
              <small className="d-block text-center">
                {currentTime.toLocaleTimeString('es-CL')}
              </small>
            </div>
            
            <div className="me-3">
              <div className="d-flex align-items-center">
                <i className="fas fa-user-circle me-2"></i>
                <div>
                  <div className="fw-semibold">{user?.nombre_usuario}</div>
                  <small className="opacity-75">{getUserTypeLabel(user?.tipo || 0)}</small>
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline-light" 
              size="sm" 
              onClick={handleLogout}
              className="ms-2"
            >
              <i className="fas fa-sign-out-alt me-1"></i>
              Salir
            </Button>
          </div>
        </Container>
      </Navbar>

      <Container fluid className="p-0">
        <Row className="g-0">
          {/* Sidebar */}
          <Col md={3} lg={2} className="sidebar">
            <div className="sidebar-content">
              <Nav className="flex-column">
                {menuItems.map((item) => (
                  <Nav.Link
                    key={item.path}
                    as={Link}
                    to={item.path}
                    className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
                  >
                    <i className={`${item.icon} me-3`}></i>
                    {item.label}
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </Col>

          {/* Main Content */}
          <Col md={9} lg={10} className="main-content">
            <div className="content-wrapper">
              {children ?? <Outlet />}
            </div>
          </Col>
        </Row>
      </Container>

      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background-color: #f8f9fa;
        }
        
        .sidebar {
          background-color: #ffffff;
          border-right: 1px solid #dee2e6;
          min-height: calc(100vh - 56px);
          box-shadow: 2px 0 4px rgba(0,0,0,0.1);
        }
        
        .sidebar-content {
          padding: 1rem 0;
        }
        
        .sidebar-item {
          padding: 0.75rem 1.5rem;
          color: #495057;
          text-decoration: none;
          border-radius: 0;
          border-left: 3px solid transparent;
          transition: all 0.3s ease;
          font-weight: 500;
        }
        
        .sidebar-item:hover {
          background-color: #e9ecef;
          color: #0066CC;
          border-left-color: #0066CC;
        }
        
        .sidebar-item.active {
          background-color: #0066CC;
          color: white;
          border-left-color: #004499;
        }
        
        .main-content {
          background-color: #f8f9fa;
          min-height: calc(100vh - 56px);
        }
        
        .content-wrapper {
          padding: 2rem;
        }
        
        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            top: 56px;
            left: -100%;
            width: 250px;
            height: calc(100vh - 56px);
            z-index: 1000;
            transition: left 0.3s ease;
          }
          
          .sidebar.show {
            left: 0;
          }
          
          .main-content {
            margin-left: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
